import {
    ClockSVG, ClockCheckSVG, ClockExclamationSVG, CalendarClockSVG,
    ClockAtLocationSVG, MultipleShiftsSGV, CircleExclamationSGV
} from '../../../utils/constants';
import { timeFormat } from '../../../utils/dateFormat';
import commonStore from '../../../library/stores/commonStore';
import AutoTimeRecordingBadge from '../../../components/AutoTimeRecordingBadge';
import FutureScheduleEntryBadge from '../../../components/FutureScheduleEntryBadge';
import { minutesToTimeInput } from '../../../utils/timeCalc';
import { getIntegrationProduct } from '../../../utils/integrationUtils';

const VacationAbsenceTemplateResolver = ({ event, t, currentView }) => {
    const titleText = `${t(event.source === 'vacation' ? "Vacation" : "Absence")} ${event.source === 'absence' ? ` (${(event.employementGrade).toFixed(2)}%)` : ""}`;
    const dateTimeRules = commonStore.getDateTimeRules();
    return (
        <div key={event.id} className="schedule_non_shift_event_template absence_vacation">
            <span>{titleText}</span>
            {event.source === 'absence' && <span>({timeFormat(event.startTime, dateTimeRules)} - {timeFormat(event.endTime, dateTimeRules)})</span>}
            {!!currentView && <span>{event.resourceName ? event.resourceName : "No Name"}</span>}
        </div>
    );
}

const AvailabilityTemplateResolver = ({ event, t, currentView }) => {
    const titleText = t(event.availability_type === 'available' ? "Available" : "Not Available");
    let clockText = event.from == '12:00 am' && event.to == '12:00 am' ? null : event.from + ' -' + event.to;
    return (
        <div key={event.id}
            className={`schedule_non_shift_event_template 
                ${event.availability_type.replace(' ', '_')} 
                ${clockText && !!currentView ? 'extra_1em' : ''}`}
        >
            <span>{titleText}</span>
            {!!currentView && <span>{event.resourceName ? event.resourceName : "No Name"}</span>}
            {clockText && <span>{clockText}</span>}
        </div>
    );
}

const RosterTemplateResolver = ({ event, t }) => {
    const dateTimeRules = commonStore.getDateTimeRules();
    return (
        <div key={event.id} className="schedule_non_shift_event_template roster_event">
            <span style={{ fontSize: '12px' }}>{timeFormat(event.startTime, dateTimeRules)} - {timeFormat(event.endTime, dateTimeRules)}</span>
        </div>
    );
};

const FreeCodeTemplateResolver = ({ event, t, currentView }) => {
    return (
        <div key={event.id} className="schedule_non_shift_event_template free_code" title={event.freeCodeName || ''}>
            <span>{event.title}</span>
            {!!currentView && <span>{event.resourceName ? event.resourceName : "No Name"}</span>}
        </div>
    );
}

const UrgentCallTemplateResolver = ({ event, t, currentView }) => {
    const titleText = event?.urgentCallDetails?.is_roster_registered ? t('Standby') : event?.is_field_operative ? t('Urgent Field') : t("Urgent Intake");
    return (
        <div key={event.id} className={`schedule_non_shift_event_template d-flex flex-row ${event?.is_field_operative ? 'urgent_call_field_operative' : 'urgent_call_customer_care'}`}>
            <span>
                {`${titleText}${event?.urgentCallDetails?.start_time
                    ? ` (${minutesToTimeInput(event.urgentCallDetails.start_time)} - ${minutesToTimeInput(event.urgentCallDetails.end_time)})`
                    : ''
                    }`}
                {event?.urgentCallDetails?.is_copied ? <i className="fas fa-circle fa-xs text-warning pt-1 d-flex align-items-center ms-2 fw-bold"></i> : <></>}
            </span>
            {event.syncStatus && <img className='ms-2' src={getIntegrationProduct(commonStore.config.integration_details.product)} width={20} height={20} />}
            {!!currentView && <span>{event.resourceName || "No Name"}</span>}
        </div>
    );
}

const getStatusSGV = (event) => {
    if (!!event.timelogCount) {
        if (event.timelogStatus === 'draft')
            return ClockAtLocationSVG({ width: 12, strokeColor: "#2550AC" });
        else if (event.hasLogFromDiffLocation)
            return CircleExclamationSGV({ width: 14, fillColor: "#FF4747" });
        else
            return ClockCheckSVG({ width: 12, strokeColor: "#038102" });
    } else {
        if (!!event.resourceId)
            return ClockSVG({ width: 12, strokeColor: "#877e7e" });
        else
            return ClockExclamationSVG({ width: 12, strokeColor: "#FF4747" });
    }
}

const ShiftTemplateResolver = ({ event, t, currentView, schedulerData, mustBeHeight, mustAddCssClass, agendaMaxEventWidth }) => {
    const shiftTime = schedulerData.behaviors.getEventTextFunc(schedulerData, event);
    const taskName = schedulerData.behaviors.getEventTaskName(schedulerData, event);
    const hasBonus = event.addBonus;
    const isRosterShift = event.createdFrom === 'roster';
    const isRecurringShift = event.createdFrom === 'recurring';
    const multiplePendingShifts = event.shiftCount && event.shiftCount > 1 ? true : false;
    const isLeave = event.workLogStatus === 'LEAVE' ? true : false;
    let divStyle = { height: mustBeHeight, opacity: (isLeave || !event.isEditable) ? '0.5' : '1' };
    if (agendaMaxEventWidth) divStyle = { ...divStyle, maxWidth: agendaMaxEventWidth };
    const shiftDetails = event.workLogStatus !== 'none' ? String(t(event.workLogStatus)).toUpperCase() : taskName;
    const isAutoScheduleMode = commonStore?.config?.client?.data?.loginRules?.autoTimelogs === 'autoschedule';
    const isAutoOrFutureRecordingShift = ((event.isFutureScheduleEntry && isAutoScheduleMode) || commonStore?.config?.client?.data?.loginRules?.allowShiftToEnableDisableAutoTimesheet) && event.allowShiftForAutoTimesheet && event.workLogStatus !== 'LEAVE';
    const autoOrFutureRecordingLabel = t(event.isFutureScheduleEntry ? 'Future' : 'Auto');
    return (
        <div key={event.id}
            className={`schedule_shift_event_template 
                ${event.isEditable ? '' : 'read_only'}
                ${event.resourceId === 0 ? 'pending' : event.workLogStatus.replace(" ", "_").toLowerCase()}`}
            style={divStyle}
        >
            {isRosterShift && <i className="fa fa-calendar-check roster_shift_icon" title={t('Roster Shift')} />}
            {isRecurringShift && <i className="me-1 fa fa-sync roster_shift_icon" title={t('Recurring Shift')} />}
            {!!currentView &&
                <span className='shift_details shift_resource_name' title={event.resourceName}>
                    {event.resourceName}
                </span>
            }
            <span className={`shift_details ${hasBonus ? 'has_bonus' : ''}`} title={shiftDetails}>
                {hasBonus && <i className="fa fa-star" style={{ color: 'goldenrod' }} title={t('Bonus')} />}
                {multiplePendingShifts && MultipleShiftsSGV({ width: 12 })}
                {multiplePendingShifts ? `${event.shiftCount} ${shiftDetails}` : shiftDetails}
            </span>
            <span className='shift_time'>
                {CalendarClockSVG({ width: 13, strokeColor: "#877e7e" })}
                <span title={shiftTime} className='ms-1'>{shiftTime}</span>
            </span>
            <span className='shift_time'>
                {!isAutoOrFutureRecordingShift && getStatusSGV(event)}
                <span title={isAutoOrFutureRecordingShift ? autoOrFutureRecordingLabel : event.timelogStatus}
                    style={{ color: isAutoOrFutureRecordingShift || (!!event.timelogCount && !event.hasLogFromDiffLocation) ? '#2550AC' : '#FF4747' }}>
                    {isAutoOrFutureRecordingShift
                        ? <div className='d-flex align-items-center'>
                            {event.isFutureScheduleEntry
                                ? <FutureScheduleEntryBadge wrapperStyle={{ display: 'flex' }} />
                                : <AutoTimeRecordingBadge wrapperStyle={{ display: 'flex' }} />}
                            <div className='ms-1'>{autoOrFutureRecordingLabel}</div>
                        </div>
                        : <div className='ms-1'>{t(event.timelogTitle)}&nbsp;{event.timelogStatus === 'draft' ? t('At work') : ''}</div>}
                </span>
                {event.isRosterShiftsPublishedAndEdited &&
                    <span title={event.isRosterShiftsPublishedAndEdited} className='ms-1'
                        style={{ color: '#FF4747' }}>
                        *{t('Edited')}
                    </span>
                }
            </span>
        </div >
    )

}

function resolver({ params }) {
    const { schedulerData, event, mustAddCssClass, mustBeHeight, agendaMaxEventWidth, t } = params;
    const currentView = schedulerData.currentView;
    if (event.source === 'vacation' || event.source === 'absence') {
        return <VacationAbsenceTemplateResolver event={event} t={t} currentView={currentView} />
    }
    else if (event.source === 'roster') {
        return <RosterTemplateResolver event={event} t={t} />
    }
    else if (event.source === 'urgent_call') {
        return <UrgentCallTemplateResolver event={event} t={t} currentView={currentView} />
    }
    else if (event.source === 'free_code') {
        return <FreeCodeTemplateResolver event={event} t={t} currentView={currentView} />
    }
    else if (event.availability_type) {
        return <AvailabilityTemplateResolver event={event} t={t} currentView={currentView} />
    }
    else {
        return <ShiftTemplateResolver
            event={event} t={t}
            currentView={currentView}
            schedulerData={schedulerData}
            agendaMaxEventWidth={agendaMaxEventWidth}
            mustAddCssClass={mustAddCssClass}
            mustBeHeight={mustBeHeight}
        />
    }
}

export default resolver;